<!-- src/lib/components/QueuePanel.svelte -->
<script lang="ts">
  import { agentStore } from '../stores/agents.svelte.ts';
  import { api } from '../api.ts';
  import { flip } from 'svelte/animate';
  import { slide } from 'svelte/transition';

  let error = $state<string | null>(null);

  async function cancelTask(agent_id: string) {
    error = null;
    try {
      await api(`/api/v1/tasks/${agent_id}`, { method: 'DELETE' });
    } catch (e) {
      error = `Cancel failed: ${String(e)}`;
    }
  }

  async function moveToFront(agent_id: string) {
    error = null;
    try {
      const min = agentStore.queued.reduce((m, t) => Math.min(m, t.sort_key), Infinity);
      await api(`/api/v1/tasks/${agent_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ sort_key: (isFinite(min) ? min : Date.now()) - 1000 }),
      });
    } catch (e) {
      error = `Reprioritize failed: ${String(e)}`;
    }
  }
</script>

<section class="panel">
  <header>
    <h2>Queue</h2>
    <span class="badge">{agentStore.queued.length}</span>
  </header>
  {#if error}
    <p class="panel-error">{error}</p>
  {/if}
  {#if agentStore.queued.length === 0}
    <p class="empty">No tasks queued.</p>
  {:else}
    <ul>
      {#each agentStore.queued as task, i (task.agent_id)}
        <li animate:flip={{ duration: 200 }} transition:slide={{ duration: 150 }}>
          <span class="index">{i + 1}</span>
          <span class="dot queued"></span>
          <span class="prompt" title={task.prompt}>
            {task.prompt.length > 72 ? task.prompt.slice(0, 72) + '…' : task.prompt}
          </span>
          <div class="actions">
            {#if i > 0}
              <button onclick={() => moveToFront(task.agent_id)} title="Move to front" class="up">↑</button>
            {/if}
            <button onclick={() => cancelTask(task.agent_id)} title="Cancel" class="cancel">✕</button>
          </div>
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style>
  .panel {
    background: oklch(0.78 0.16 85 / 0.03);
    border: 1px solid oklch(0.78 0.16 85 / 0.12);
    border-radius: 10px;
    padding: 1rem 1.25rem;
  }
  header {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    margin-bottom: 0.875rem;
  }
  h2 {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: oklch(0.78 0.16 85);
    margin: 0;
  }
  .badge {
    background: oklch(0.78 0.16 85 / 0.15);
    color: oklch(0.78 0.16 85);
    border-radius: 12px;
    padding: 0.1rem 0.5rem;
    font-size: 0.7rem;
    font-family: 'JetBrains Mono', monospace;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }
  li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid oklch(0.78 0.16 85 / 0.08);
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    transition: background 200ms ease, border-color 200ms ease;
  }
  li:hover {
    background: oklch(0.78 0.16 85 / 0.05);
    border-color: oklch(0.78 0.16 85 / 0.20);
  }
  .index {
    color: oklch(0.78 0.16 85 / 0.40);
    font-size: 0.7rem;
    font-family: 'JetBrains Mono', monospace;
    min-width: 1.25rem;
  }
  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .dot.queued {
    background: oklch(0.78 0.16 85);
    box-shadow: 0 0 6px oklch(0.78 0.16 85 / 0.60);
    animation: amber-pulse 2.5s ease-in-out infinite;
  }
  @keyframes amber-pulse {
    0%, 100% { opacity: 1; box-shadow: 0 0 6px oklch(0.78 0.16 85 / 0.60); }
    50% { opacity: 0.5; box-shadow: 0 0 2px oklch(0.78 0.16 85 / 0.20); }
  }
  .prompt {
    flex: 1;
    font-size: 0.82rem;
    color: #c0c4d0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-family: system-ui, sans-serif;
  }
  .actions { display: flex; gap: 0.25rem; }
  .actions button {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    color: #888;
    padding: 0.2rem 0.45rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.7rem;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .actions .up:hover { background: oklch(0.55 0.20 265 / 0.15); border-color: oklch(0.58 0.22 265); color: oklch(0.58 0.22 265); }
  .actions .cancel:hover { background: oklch(0.62 0.26 25 / 0.15); border-color: oklch(0.62 0.26 25); color: #ff6464; }
  .empty { color: rgba(255, 255, 255, 0.2); font-size: 0.82rem; font-style: italic; margin: 0.5rem 0; }
  .panel-error { color: oklch(0.78 0.16 85); font-size: 0.75rem; font-family: 'JetBrains Mono', monospace; margin: 0 0 0.5rem; }
</style>
